/**
 * 경량 오디오 매니저 — WebAudio 합성 SFX + 생성형 앰비언트 (외부 에셋 0개).
 *
 * CC0 에셋 소싱(Kenney)은 직링크가 없어 1회 시도 후 합성 폴백으로 확정(계획 §③).
 * 앰비언트 BGM도 트랙 파일 대신 절차 생성: 필터링된 노이즈(바람) + 오각음계 풍경(윈드차임)
 * 종소리를 랜덤 간격으로 — 국악 풍경(風磬) 느낌, 라이선스·다운로드 0.
 *
 * 초기화 시 localStorage(game_settings_v1)에서 soundMuted/sfxVolume/bgmVolume을 읽는다 —
 * configure()가 GameEntry에서만 불려 로그인 화면이 음소거를 무시하던 버그(코드리뷰 #2) 수정.
 *
 * 브라우저 자동재생 정책: AudioContext는 유저 제스처 전에는 suspended — 그 상태에서
 * 예약한 소리는 첫 제스처에 한꺼번에 터지므로(코드리뷰 #5) running이 아니면 스킵한다.
 */
import { GAME_SETTINGS_KEY } from '@/config/storageKeys';
import { SOUND_CATALOG, SFX_GROUPS } from './soundCatalog';
import BGM_JSON from './bgmTracks.json';

let ctx = null;
let sfxVolume = 0.8;
let bgmVolume = 0.5;
let muted = false;
let ambienceVolume = 0.35; // 환경음(루프 mp3) — BGM 과 다른 손잡이다. 밑에 깔리는 소리라 기본이 낮다

/**
 * 자동화 브라우저(캡처 봇)에서는 소리를 통째로 잠근다.
 * 사람 접속에는 영향이 없다 — localStorage 를 안 건드리고 런타임 변수만 고정한다.
 * `?mute=1` 은 수동 탈출구(puppeteer.connect 로 붙어 navigator.webdriver 가 false 인 경우).
 * `?mute=0` 은 봇이어도 소리를 켠다(사운드 자체를 검증하는 캡처용).
 */
const SILENT_BOT = (() => {
  if (typeof window === 'undefined') return true;
  try {
    const q = new URLSearchParams(window.location.search).get('mute');
    if (q === '1') return true;
    if (q === '0') return false;
  } catch { /* URL 파싱 실패는 무시 */ }
  // VS Code 내장 브라우저(Electron)도 webdriver 를 켜지만 사람이 플레이하는 창이다.
  return navigator.webdriver === true && !/Electron\//.test(navigator.userAgent);
})();

// 설정 초기 로드 — 키의 정본은 `config/storageKeys.js`. 이후 변경은 configure()로 수신.
try {
  const raw = window.localStorage.getItem(GAME_SETTINGS_KEY);
  if (raw) {
    const s = JSON.parse(raw);
    if (typeof s.sfxVolume === 'number') sfxVolume = s.sfxVolume;
    if (typeof s.bgmVolume === 'number') bgmVolume = s.bgmVolume;
    if (typeof s.soundMuted === 'boolean') muted = s.soundMuted;
    if (typeof s.ambienceVolume === 'number') ambienceVolume = s.ambienceVolume;
  }
} catch (err) { /* 설정 없으면 기본값 */ }
if (SILENT_BOT) muted = true;

function ensureCtx() {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => { /* noop */ });
  return ctx;
}

// running이 아닐 때 예약하면 첫 제스처에 밀린 소리가 일괄 재생됨 — 재생 가능 상태에서만 스케줄
function runningCtx() {
  const ac = ensureCtx();
  return ac && ac.state === 'running' ? ac : null;
}

const LEGACY_SFX_ALIAS = {
  click: 'ui.click',
  chop: 'world.harvest.chop',
  pickup: 'loot.pickup',
};

// ── BGM 트랙 (front/public/audio/bgm/LICENSE.txt) ──
// 출처 두 갈래 — ①CC BY(Kevin MacLeod/Matthew Pablo, 표기 의무 있음) ②자체 생성(ACE-Step,
// Apache-2.0, 표기 의무 없음 · 프롬프트/시드는 ai_assets/audio/*.json). 원곡 OST는 저작권상 사용 불가.
/**
 * 로그인 화면 크레딧 — CC BY 는 표기가 «조건»이라 곡 목록과 같은 자리에 둔다.
 * login 트랙을 바꾸면 여기도 같이 바꾼다(LICENSE.txt ① 참조).
 */
export const LOGIN_BGM_CREDIT = {
  titleKo: '서리 왈츠',
  title: 'Frost Waltz',
  artist: 'Kevin MacLeod',
  license: 'CC BY 4.0',
  licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
  alsoKo: '그 밖의 곡 — Kevin MacLeod (CC BY 4.0) · Matthew Pablo (CC BY 3.0) · 일부 자체 생성',
};

/** 곡 출처 — 배경음 창 목록 줄의 팝오버가 읽는다. CC BY 는 표기가 사용 조건이다. */
export const BGM_SOURCES = {
  kevin: { label: 'Kevin MacLeod', site: 'incompetech.com', license: 'CC BY 4.0', url: 'http://creativecommons.org/licenses/by/4.0/' },
  pablo: { label: 'Matthew Pablo', site: 'matthewpablo.com', license: 'CC BY 3.0', url: 'http://creativecommons.org/licenses/by/3.0/' },
  ace: { label: '자체 생성', site: 'ACE-Step v1 3.5B', license: 'Apache-2.0', url: 'https://www.apache.org/licenses/LICENSE-2.0' },
  studio: { label: '자체 작곡', site: 'tool_Music · GeneralUser GS 악기', license: '자체 저작 · 악기 샘플 GeneralUser GS v2.0', url: '' },
};

// 곡 대장 — 정본은 bgmTracks.json. key = mp3 파일명, orig = 원제(외부 곡) 또는 악기 구성(자체 생성), by = BGM_SOURCES 키.
// 배경음 창의 삭제 버튼(개발 서버 전용)이 이 배열에서도 줄을 빼므로 사본이 아니라 같은 배열을 쓴다.
export const BGM_TRACKS = BGM_JSON;

/** 분류 순서 — 필터 패널의 «분류» 축이 이 차례로 그린다. */
export const BGM_GROUPS = ['마을', '필드', '잔잔', '작업', '던전', '전투'];

export const BGM = {
  login: ['/audio/bgm/frost_waltz.mp3'],
  get field() { return BGM_TRACKS.map((t) => `/audio/bgm/${t.key}.mp3`); },
};

/**
 * 설정 → 실제 재생 목록. **축이 「한 곡 고르기」에서 「켤 곡 고르기」로 바뀌었다**(2026-08-16).
 *
 * 옛 축(fieldBgmTrack: 'rotation'|키|'off')은 곡이 13개가 되자 못 쓰게 됐다 —
 * 「이 곡 빼고 다」를 표현할 수가 없어서 마음에 안 드는 곡 하나 때문에 로테이션을 통째로
 * 버리고 단일곡으로 가야 했다. 새 축은 켜진 키의 배열 하나다: 전부 켜면 로테이션,
 * 하나만 켜면 그 곡 반복, 다 끄면 배경음 끄기 — **상태 셋이 같은 손잡이 하나에서 나온다.**
 *
 * 옛 값은 지우지 않고 여기서 읽어 유도한다(저장은 안 한다 — 유저가 한 번이라도 토글하면
 * bgmTracks 가 생기고 그때부터 그쪽이 답이다).
 */
/**
 * 곡 «전체»의 사용자 순서. 켜고 끔과는 다른 축이다 — 꺼 둔 곡도 자리를 지켜야
 * 다시 켰을 때 엉뚱한 데로 튀지 않는다.
 *
 * 저장값(bgmOrder)에 없는 새 곡은 «뒤»에 붙인다. 앞에 끼우면 업데이트 때마다 로테이션
 * 머리가 바뀌어서, 유저가 정해 둔 첫 곡이 조용히 밀려난다.
 */
export function orderedBgmKeys(settings = {}) {
  const known = BGM_TRACKS.map((t) => t.key);
  const kset = new Set(known);
  const saved = Array.isArray(settings.bgmOrder)
    ? settings.bgmOrder.filter((k) => kset.has(k))
    : [];
  const seen = new Set(saved);
  return [...saved, ...known.filter((k) => !seen.has(k))];
}

export function resolveFieldBgm(settings = {}) {
  const { bgmTracks, fieldBgmTrack } = settings;
  const known = new Set(BGM_TRACKS.map((t) => t.key));
  if (Array.isArray(bgmTracks)) {
    // 목록에 없는 키는 버린다 — 곡을 지운 뒤에도 저장값이 남아 404 를 재생하려 든다
    // ★ 재생 «차례»는 bgmTracks 의 배열 순서가 아니라 bgmOrder 가 정한다.
    //   전에는 켜는 순서가 곧 재생 순서였는데, 그러면 곡을 하나 껐다 켜는 것만으로
    //   그 곡이 목록 끝으로 밀렸다 — 「순서를 정한다」와 「켠다」가 한 손잡이였던 탓이다.
    const on = new Set(bgmTracks.filter((k) => known.has(k)));
    return orderedBgmKeys(settings).filter((k) => on.has(k)).map((k) => `/audio/bgm/${k}.mp3`);
  }
  if (fieldBgmTrack === 'off') return [];
  if (fieldBgmTrack && fieldBgmTrack !== 'rotation' && known.has(fieldBgmTrack)) {
    return [`/audio/bgm/${fieldBgmTrack}.mp3`];
  }
  return BGM.field;
}

/** 설정 → 켜진 키 배열(UI 가 체크 상태를 그릴 때 쓴다). resolveFieldBgm 과 같은 규칙이어야 한다. */
export function enabledBgmKeys(settings = {}) {
  return resolveFieldBgm(settings).map((u) => u.split('/').pop().replace('.mp3', ''));
}

// ── 환경음(루프 mp3) — BGM 과 다른 층이다 ──────────────────────────────────
// 갈리는 자리: BGM 은 곡이 끝나면 다음 곡으로 넘어가고(로테이션), 환경음은 한 파일을
// 계속 돈다(loop). 볼륨도 따로다 — 밑에 깔리는 소리가 곡과 같은 크기면 곡을 먹는다.
// 곡과 동시에 난다(둘 다 켜 두는 게 정상 상태).
export const AMBIENCE_OPTIONS = [
  { key: 'off', label: '환경음 끄기', desc: '기본' },
  { key: 'amb_forest_day', label: '숲 바람', desc: '들새·풀벌레·풀 스치는 바람' },
  { key: 'amb_night', label: '밤 풀벌레', desc: '숲 귀뚜라미·먼 새' },
  { key: 'amb_rain', label: '비', desc: '노면에 듣는 비·먼 차 소리' },
  { key: 'amb_cave', label: '동굴', desc: '어둡게 깔리는 저역' },
  { key: 'amb_river', label: '냇가', desc: '바위에 찰박이는 물' },
  { key: 'amb_village', label: '마을', desc: '골목 안뜰·먼 차·아이들' },
  // 전부 30초 루프다(옛 60초 AI 생성본을 2026-08-30 에 통째로 갈았다). 이음매는 앞뒤 2초를
  // 겹쳐 페이드해 없앴으므로 그냥 loop 로 돌리면 된다.
  //   굽는 법: python scripts/make_ambience_loops.py --only amb_river
  //   소스: Sonniss GDC 2026 번들(로열티프리·표기 불필요) — ai_assets/audio/sfx_sonniss/
];

function ambienceUrl(key) {
  if (!key || key === 'off') return null;
  return `/audio/ambience/${key}.mp3`;
}


let bgmEl = null; // 단일 HTMLAudio 재사용 — 미선언이면 strict mode ReferenceError가 try에 먹혀 무음이 된다
let bgmSig = ''; // 재생 중인 리스트 시그니처 — 동일 리스트 재호출 무시용
let bgmList = [];
let bgmIndex = 0;
let bgmPendingPlay = false; // 자동재생 거부 시 다음 제스처에서 재시도

let ambEl = null; // 환경음 전용 HTMLAudio — BGM 엘리먼트와 공유하지 않는다(동시에 나야 한다)
let ambKey = ''; // 재생 중인 환경음 키 — 같은 키 재호출은 무시(재시작하면 소리가 툭 끊긴다)
let ambPendingPlay = false;

function playAmbienceTrack() {
  if (!ambEl || !ambEl.src) return;
  ambEl.volume = 0;
  ambEl.muted = muted;
  ambEl.play().then(() => {
    ambPendingPlay = false;
    const t0 = performance.now();
    const fade = () => { // 3초 페이드 인 — 환경음이 툭 켜지면 「소리가 났다」로 들린다
      if (!ambEl) return;
      const k = Math.min(1, (performance.now() - t0) / 3000);
      ambEl.volume = ambienceVolume * k;
      if (k < 1) window.requestAnimationFrame(fade);
    };
    fade();
  }).catch(() => { ambPendingPlay = true; });
}

let nowPlayingSrc = null;
let bgmUserPaused = false; // 사용자가 직접 멈춤(HUD·이어폰) — 목록이 바뀌어도 저절로 다시 틀지 않는다
const bgmHistory = []; // 「이전 곡」용 — 재생한 src 를 최근 20개까지

/** 곡 src → { title, artist } — 잠금화면·이어폰 표시와 HUD 위젯이 같이 쓴다. */
export function bgmInfo(src) {
  if (!src) return null;
  const key = src.split('/').pop().replace('.mp3', '');
  if (key === 'frost_waltz') return { key, title: '서리 왈츠', artist: 'Kevin MacLeod' };
  const t = BGM_TRACKS.find((o) => o.key === key);
  const by = BGM_SOURCES[t?.by];
  return { key, title: t?.label || key, artist: [t?.orig, by?.label].filter(Boolean).join(' · ') };
}

function emitBgmState() {
  try {
    window.dispatchEvent(new CustomEvent('audio:bgmstate', {
      detail: { src: nowPlayingSrc, paused: !bgmEl || bgmEl.paused },
    }));
  } catch { /* noop */ }
}

// 미디어 세션 — PWA 를 백그라운드로 둔 채 잠금화면·알림창·블루투스 이어폰 버튼으로 곡을 넘긴다.
// 핸들러는 한 번만 단다(곡마다 다시 달 필요 없다). 지원 안 하는 브라우저는 조용히 건너뛴다.
let mediaSessionBound = false;
// 곡 정보 대신 알림창에 띄울 내용 {title, artist, artwork(512px PNG URL)} — 곡이 바뀌어도 유지. 원정 현황(mercLive)이 쓴다.
let mediaOverlay = null;
function syncMediaSession() {
  const ms = typeof navigator !== 'undefined' ? navigator.mediaSession : null;
  if (!ms) return;
  if (!mediaSessionBound) {
    mediaSessionBound = true;
    const on = (action, fn) => { try { ms.setActionHandler(action, fn); } catch { /* 미지원 액션 */ } };
    on('play', () => audioManager.resumeBgm());
    on('pause', () => audioManager.pauseBgm());
    on('nexttrack', () => audioManager.nextBgm());
    on('previoustrack', () => audioManager.prevBgm());
    on('seekto', (d) => audioManager.seekBgm(d.seekTime));
  }
  const info = bgmInfo(nowPlayingSrc);
  try {
    ms.metadata = info && typeof window.MediaMetadata === 'function' ? new window.MediaMetadata({
      title: mediaOverlay?.title || info.title,
      artist: mediaOverlay ? [mediaOverlay.artist, info.title].filter(Boolean).join(' · ') : info.artist,
      album: '브로큰 배경음',
      artwork: mediaOverlay?.artwork ? [{ src: mediaOverlay.artwork, sizes: '512x512', type: 'image/png' }] : [
        { src: '/icons/app-192.png', sizes: '192x192', type: 'image/png' },
        { src: '/icons/app-512.png', sizes: '512x512', type: 'image/png' },
      ],
    }) : null;
    ms.playbackState = !bgmEl || !nowPlayingSrc ? 'none' : (bgmEl.paused ? 'paused' : 'playing');
  } catch { /* noop */ }
}

// BGM 엘리먼트 보장 — startBgm 과 playBgmNow 두 진입점이 쓴다. 두 벌로 두면 「다음 곡」
// 핸들러가 한쪽에만 붙어, 미리듣기로 시작한 세션에서 곡이 끝나도 다음으로 안 넘어간다.
// ── 다음 곡을 «어떻게» 고르나 ────────────────────────────────────────────────
// 축이 둘이다. 「차례를 한 번 섞는다」(설정의 [무작위로 섞기] — `bgmOrder` 를 재배열)와
// 「매번 무작위로 고른다」(이 모드)는 **다른 일**이다. 전자는 섞은 뒤로는 늘 같은 차례로
// 돌고, 후자는 들을 때마다 차례가 없다. 전자만 있으면 「한 바퀴 돌면 또 그 차례」가 된다.
//
// ⚠ 직전 곡을 다시 뽑지 않는다 — 무작위는 같은 곡을 연달아 낼 수 있는데, 듣는 쪽에는
//   그게 «무작위»가 아니라 «고장»으로 들린다(곡이 안 넘어간 것과 구분이 안 된다).
// ⚠ 2곡 이하면 순차와 결과가 같다(1곡은 loop, 2곡은 번갈아) — 굳이 난수를 돌리지 않는다.
let bgmShuffle = false;

function pickNextIndex() {
  const n = bgmList.length;
  if (!n) return 0;
  if (!bgmShuffle || n <= 2) return (bgmIndex + 1) % n;
  const cur = bgmIndex % n;
  let pick = cur;
  while (pick === cur) pick = Math.floor(Math.random() * n);
  return pick;
}

function ensureBgmEl() {
  if (bgmEl) return bgmEl;
  bgmEl = new Audio();
  bgmEl.addEventListener('ended', () => {
    bgmIndex = pickNextIndex();
    playBgmTrack();
  });
  const onState = () => { syncMediaSession(); emitBgmState(); };
  let lastPos = 0;
  bgmEl.addEventListener('timeupdate', () => {
    const now = performance.now();
    if (now - lastPos < 1000 || !navigator.mediaSession?.setPositionState || !Number.isFinite(bgmEl.duration)) return;
    lastPos = now;
    try { navigator.mediaSession.setPositionState({ duration: bgmEl.duration, position: bgmEl.currentTime, playbackRate: 1 }); } catch { /* noop */ }
  });
  bgmEl.addEventListener('play', onState);
  bgmEl.addEventListener('pause', onState);
  return bgmEl;
}

// src 를 주면 그 곡을, 안 주면 목록의 현재 위치를 재생한다(미리듣기는 목록 밖 곡도 튼다).
function playBgmTrack(overrideSrc) {
  if (!bgmEl) return;
  const src = overrideSrc || (bgmList.length ? bgmList[bgmIndex % bgmList.length] : null);
  if (!src) return;
  if (nowPlayingSrc && nowPlayingSrc !== src) {
    bgmHistory.push(nowPlayingSrc);
    if (bgmHistory.length > 20) bgmHistory.shift();
  }
  bgmUserPaused = false;
  bgmEl.src = src;
  nowPlayingSrc = src;
  try { window.dispatchEvent(new CustomEvent('audio:bgm', { detail: { src: nowPlayingSrc } })); } catch (err) { /* noop */ }
  syncMediaSession();
  // 숨긴 탭에서 소리 크기 0 으로 시작하면 크롬이 재생을 끊어(AbortError) 폰 미디어 알림까지 사라진다 — 페이드 없이 제 크기로.
  const hidden = typeof document !== 'undefined' && document.hidden;
  bgmEl.volume = hidden ? bgmVolume : 0;
  bgmEl.muted = muted;
  bgmEl.play().then(() => {
    bgmPendingPlay = false;
    if (hidden) return;
    // 2초 페이드 인
    const target = bgmVolume;
    const t0 = performance.now();
    const fade = () => {
      if (!bgmEl) return;
      const k = Math.min(1, (performance.now() - t0) / 2000);
      bgmEl.volume = target * k;
      if (k < 1) window.requestAnimationFrame(fade);
    };
    fade();
  }).catch(() => { bgmPendingPlay = true; });
}

// ── 효과음(실녹음 mp3) — 첫 제스처(unlock)에 카탈로그 전부를 받아 두고, 못 받은 소리는 소리 없이 넘어간다 ──
const sampleCache = new Map(); // key → AudioBuffer | Promise(받는 중) | 'failed'
const LATE_PLAY_S = 0.35; // 받기·잠금 해제가 이 안에 끝나면 그 소리를 늦게라도 낸다. 넘으면 버린다(밀린 소리 일괄 재생 방지)

function sfxUrl(id) {
  return `/audio/sfx/${id.replace(/\./g, '_')}.mp3`;
}

function fetchSfxBytes(id) {
  return fetch(sfxUrl(id)).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))));
}

// 첫 클릭이 «받는 중»으로 늦지 않게 바이트만 미리 받는다(오디오 엔진은 제스처 전에 만들지 않는다). 디코딩은 loadSample 이 한 번.
const sfxBytes = new Map(); // id → Promise<ArrayBuffer>
if (typeof window !== 'undefined' && !SILENT_BOT) {
  const prefetch = () => Object.keys(SOUND_CATALOG).forEach((id) => {
    const p = fetchSfxBytes(id);
    p.catch(() => { /* loadSample 이 실패로 처리 */ });
    sfxBytes.set(id, p);
  });
  if (document.readyState === 'complete') setTimeout(prefetch, 0);
  else window.addEventListener('load', prefetch, { once: true });
}

/** 버퍼 약속 — 받은 버퍼, 실패면 null. 같은 id 는 한 번만 받는다. */
function loadSample(ac, id) {
  const hit = sampleCache.get(id);
  if (hit instanceof AudioBuffer || hit instanceof Promise) return Promise.resolve(hit);
  if (hit === 'failed') return Promise.resolve(null);
  const bytes = sfxBytes.get(id) ?? fetchSfxBytes(id);
  sfxBytes.delete(id); // 디코딩이 ArrayBuffer 를 떼어 가므로 재사용 불가
  const p = bytes
    .then((buf) => ac.decodeAudioData(buf))
    .then((decoded) => { sampleCache.set(id, decoded); return decoded; })
    .catch(() => { sampleCache.set(id, 'failed'); return null; });
  sampleCache.set(id, p);
  return p;
}

/** 받아 둔 버퍼. 없으면 받아오기 시작하고 null. */
function sampleBuffer(ac, id) {
  const hit = sampleCache.get(id);
  if (hit instanceof AudioBuffer) return hit;
  if (!hit) loadSample(ac, id);
  return null;
}

function playSample(ac, buffer, gainScale, pitch = 1) {
  const src = ac.createBufferSource();
  src.buffer = buffer;
  src.playbackRate.value = pitch;
  const g = ac.createGain();
  g.gain.value = Math.max(0, Math.min(1, sfxVolume * gainScale));
  src.connect(g).connect(ac.destination);
  src.start();
}

// ── 샘플 루프(열차 주행음 등) — 효과음 볼륨·음소거를 따른다. 합성 폴백 없음(파일이 없으면 무음) ──
const sfxLoops = new Set(); // { id, base, scale, src, g, stopped }

function loopLevel(h) {
  return muted ? 0 : Math.max(0, Math.min(1, sfxVolume * h.scale));
}

/** 버퍼가 받아져 있고 컨텍스트가 돌고 있으면 재생을 건다. 아니면 다음 setGain 때 다시 시도한다. */
function startLoop(h) {
  if (h.src || h.stopped) return;
  const ac = runningCtx();
  if (!ac) return;
  const buf = sampleBuffer(ac, h.id);
  if (!buf) return;
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  // mp3 앞뒤 인코더 여백을 건너 이음매에 무음이 끼지 않게 한다
  const pad = Math.min(0.05, buf.duration / 8);
  src.loopStart = pad;
  src.loopEnd = buf.duration - pad;
  const g = ac.createGain();
  g.gain.value = 0;
  g.gain.setTargetAtTime(loopLevel(h), ac.currentTime, 0.3);
  src.connect(g).connect(ac.destination);
  src.start(0, pad);
  h.src = src;
  h.g = g;
}

export const audioManager = {
  configure({ sfx, bgm, mute, ambience } = {}) {
    if (typeof sfx === 'number') sfxVolume = Math.max(0, Math.min(1, sfx));
    if (typeof bgm === 'number') {
      bgmVolume = Math.max(0, Math.min(1, bgm));
      if (bgmEl) bgmEl.volume = bgmVolume;
    }
    if (typeof ambience === 'number') {
      ambienceVolume = Math.max(0, Math.min(1, ambience));
      if (ambEl) ambEl.volume = ambienceVolume;
    }
    if (typeof mute === 'boolean') {
      muted = SILENT_BOT || mute; // 봇에서는 설정이 뭘 밀어넣든 음소거를 유지한다
      if (bgmEl) bgmEl.muted = muted;
      if (ambEl) ambEl.muted = muted;
    }
    sfxLoops.forEach((h) => { if (h.g) h.g.gain.setTargetAtTime(loopLevel(h), h.g.context.currentTime, 0.05); });
  },
  /** 샘플을 미리 받아 둔다 — 첫 재생이 무음으로 빠지지 않게(합성 폴백이 없는 id 용). */
  preload(id) {
    const ac = runningCtx();
    if (ac && SOUND_CATALOG[id]) sampleBuffer(ac, id);
  },
  /**
   * 샘플 루프 — 반환 핸들 { setGain(0~1), stop() }. 실제 음량 = 효과음 볼륨 × 카탈로그 gain × setGain 값.
   * 파일이 아직 안 받아졌거나 오디오가 잠겨 있으면 setGain 을 부를 때마다 다시 시작을 시도한다.
   * 호출자는 반드시 stop() 을 부른다(하차·언마운트).
   */
  loop(id, gain = 1) {
    const base = SOUND_CATALOG[id]?.gain ?? 1;
    const h = { id, base, scale: base * gain, src: null, g: null, stopped: !SOUND_CATALOG[id] };
    if (!h.stopped) { sfxLoops.add(h); try { startLoop(h); } catch { /* noop */ } }
    return {
      setGain(v) {
        if (h.stopped) return;
        h.scale = h.base * Math.max(0, v);
        try {
          startLoop(h);
          if (h.g) h.g.gain.setTargetAtTime(loopLevel(h), h.g.context.currentTime, 0.4);
        } catch { /* noop */ }
      },
      stop() {
        if (h.stopped) return;
        h.stopped = true;
        sfxLoops.delete(h);
        if (!h.src) return;
        try {
          const t = h.g.context.currentTime;
          h.g.gain.setTargetAtTime(0, t, 0.08);
          h.src.stop(t + 0.4);
        } catch { /* noop */ }
      },
    };
  },
  unlock() {
    ensureCtx();
    // 이전에 자동재생이 거부된 BGM·환경음이 있으면 제스처 시점에 재시도
    if (bgmPendingPlay && !bgmUserPaused) playBgmTrack();
    if (ambPendingPlay) playAmbienceTrack();
    // 첫 제스처 순간엔 resume() 이 아직 안 끝나 suspended 다 — 풀린 뒤에 카탈로그를 받는다
    const ac = ensureCtx();
    if (ac) Promise.resolve(ac.state === 'running' || ac.resume()).then(() => {
      Object.keys(SOUND_CATALOG).forEach((id) => sampleBuffer(ac, id));
    }).catch(() => { /* noop */ });
  },
  // 환경음 — 키 하나로 켜고 끈다('off'/빈값이면 정지). 같은 키 재호출은 무시한다.
  setAmbience(key) {
    const next = key && key !== 'off' ? key : '';
    if (ambKey === next) return;
    ambKey = next;
    if (!next) {
      if (ambEl) { try { ambEl.pause(); } catch (err) { /* noop */ } ambEl.removeAttribute('src'); }
      ambPendingPlay = false;
      return;
    }
    try {
      if (!ambEl) {
        ambEl = new Audio();
        ambEl.loop = true; // 환경음은 끝나면 다음이 없다 — 그 자리에서 다시 돈다
      }
      ambEl.src = ambienceUrl(next);
      playAmbienceTrack();
    } catch (err) { /* noop */ }
  },
  // 효과음 재생 — id는 soundCatalog 택소노미('ui.click', 'player.jump' 등)
  play(id, opts) {
    // 계측 — window.__sfxTrace 를 배열로 두면 호출을 남긴다(헤드리스는 소리를 못 들으므로 «언제·얼마나»를 여기서 잰다)
    if (typeof window !== 'undefined' && Array.isArray(window.__sfxTrace)) window.__sfxTrace.push([id, +(opts?.gain ?? 1).toFixed(2), Math.round(performance.now())]);
    if (muted) return;
    const key = SOUND_CATALOG[id] ? id : LEGACY_SFX_ALIAS[id];
    const entry = SOUND_CATALOG[key];
    if (!entry) return;
    const ac = ensureCtx();
    if (!ac) return;
    const gain = (entry.gain ?? 1) * (opts?.gain ?? 1);
    const pitch = opts?.pitch ?? 1;
    try {
      const buf = ac.state === 'running' ? sampleBuffer(ac, key) : null;
      if (buf) { playSample(ac, buf, gain, pitch); return; }
      // 잠금 해제 중이거나 아직 안 받은 소리 — 짧게 기다렸다 낸다
      const t0 = performance.now();
      Promise.all([ac.state === 'running' || ac.resume(), loadSample(ac, key)]).then(([, b]) => {
        if (b && !muted && ac.state === 'running' && performance.now() - t0 < LATE_PLAY_S * 1000) playSample(ac, b, gain, pitch);
      }).catch(() => { /* noop */ });
    } catch (err) { /* 오디오 실패는 게임 흐름을 막지 않는다 */ }
  },
  // BGM — urls: 문자열 또는 배열(순차 로테이션). 같은 리스트로 재호출하면 무시(중복 시작 방지).
  // 자동재생 거부되면 pending으로 두고 unlock()(첫 제스처)에서 재시도.
  startBgm(urls) {
    const list = Array.isArray(urls) ? urls : [urls];
    if (!list.length || !list[0]) return;
    const sig = list.join('|');
    if (bgmSig === sig) return;
    bgmSig = sig;
    try {
      ensureBgmEl();
      bgmList = list;
      emitBgmState();
      bgmEl.loop = list.length === 1;
      // 목록이 바뀌어도 **지금 나오는 곡이 새 목록에 살아 있으면 안 끊는다.**
      // 설정에서 다른 곡을 켜고 끌 때마다 듣던 곡이 처음으로 튀면 그 자체가 고장으로 읽힌다.
      const keep = nowPlayingSrc ? list.indexOf(nowPlayingSrc) : -1;
      if (keep >= 0 && (bgmUserPaused || (!bgmEl.paused && !bgmPendingPlay))) {
        bgmIndex = keep;
        return;
      }
      bgmIndex = 0;
      playBgmTrack();
    } catch (err) { /* noop */ }
  },
  // 「이 곡을 지금」 — 목록은 안 건드리고 나오는 곡만 갈아 끼운다(설정 ▸ 사운드의 ▶).
  // 켜 둔 곡이면 그 자리로 점프해 로테이션이 그대로 이어지고, **꺼 둔 곡이면 한 번만
  // 끼워 듣고** 끝난 뒤 원래 목록으로 돌아온다 — 들어 보는 행위가 목록을 바꾸면
  // 그건 미리듣기가 아니라 설정 변경이다(그건 [단독] 버튼의 일이다).
  playBgmNow(url) {
    if (!url) return;
    ensureBgmEl();
    const at = bgmList.indexOf(url);
    if (at >= 0) {
      bgmIndex = at;
      bgmEl.loop = bgmList.length === 1;
      playBgmTrack();
      return;
    }
    bgmEl.loop = false; // 목록 밖 곡은 반복하지 않는다(끝나면 ended → 목록의 다음 곡)
    playBgmTrack(url);
  },
  // 다음 곡 — 로테이션이 13곡이 되면서 「지금 이 곡 말고」를 누를 자리가 필요해졌다.
  //   랜덤 모드면 «무작위로» 넘어간다 — 「다음」이 모드마다 다른 뜻이면 그게 거짓말이다.
  nextBgm() {
    if (!bgmList.length) return;
    bgmIndex = pickNextIndex();
    playBgmTrack();
  },
  /**
   * 랜덤 재생 모드. 설정(`bgmShuffle`)이 바뀔 때 한 번씩 흘려 넣는다.
   * ⚠ **지금 나오는 곡은 안 건드린다** — 모드를 켰다고 듣던 곡이 끊기면 그건 「모드 변경」이
   *   아니라 「다음 곡」이다. 다음 곡부터 적용된다.
   */
  setBgmShuffle(on) { bgmShuffle = !!on; },
  /** 이전 곡 — 재생 기록을 거슬러 간다. 기록이 없으면 지금 곡을 처음부터. */
  prevBgm() {
    if (!bgmEl) return;
    if (bgmEl.currentTime > 3 || !bgmHistory.length) {
      bgmEl.currentTime = 0;
      if (bgmEl.paused) audioManager.resumeBgm();
      return;
    }
    const src = bgmHistory.pop();
    const at = bgmList.indexOf(src);
    if (at >= 0) bgmIndex = at;
    nowPlayingSrc = null; // 기록에 다시 쌓이지 않게
    playBgmTrack(src);
  },
  /** 사용자 멈춤 — HUD 위젯·이어폰 버튼. 목록이 바뀌어도 저절로 다시 안 튼다. */
  pauseBgm() {
    if (!bgmEl) return;
    bgmUserPaused = true;
    bgmPendingPlay = false;
    try { bgmEl.pause(); } catch { /* noop */ }
  },
  resumeBgm() {
    ensureBgmEl();
    bgmUserPaused = false;
    if (!bgmEl.src) { if (bgmList.length) playBgmTrack(); return; }
    bgmEl.muted = muted;
    bgmEl.volume = bgmVolume;
    bgmEl.play().catch(() => { bgmPendingPlay = true; });
  },
  isBgmPaused() {
    return !bgmEl || bgmEl.paused;
  },
  /** 폰 미디어 알림창 내용을 덮어쓴다({title, artist, artwork}) — null 이면 곡 정보로 돌아간다. */
  setMediaOverlay(overlay) {
    mediaOverlay = overlay;
    syncMediaSession();
  },
  /** 지금 곡 재생 위치 { cur, dur } (초). 곡이 없거나 길이를 모르면 dur 0. */
  getBgmTime() {
    if (!bgmEl) return { cur: 0, dur: 0 };
    return { cur: bgmEl.currentTime || 0, dur: Number.isFinite(bgmEl.duration) ? bgmEl.duration : 0 };
  },
  /** 지금 곡을 t 초로 옮긴다(배경음 창 스크러버 · 잠금화면 탐색). */
  seekBgm(t) {
    if (!bgmEl || !Number.isFinite(bgmEl.duration)) return;
    bgmEl.currentTime = Math.max(0, Math.min(bgmEl.duration - 0.1, t));
  },
  /** 배경음 한 곡을 대장·재생 목록에서 뺀다(파일은 개발 서버가 지운다). 나오던 곡이면 다음 곡으로. */
  forgetBgm(key) {
    const at = BGM_TRACKS.findIndex((t) => t.key === key);
    if (at >= 0) BGM_TRACKS.splice(at, 1);
    const src = `/audio/bgm/${key}.mp3`;
    const wasNow = nowPlayingSrc === src;
    bgmList = bgmList.filter((u) => u !== src);
    bgmSig = bgmList.join('|');
    if (wasNow) {
      if (bgmList.length) { bgmIndex %= bgmList.length; playBgmTrack(); } else audioManager.stopBgm();
    }
    try { window.dispatchEvent(new Event('audio:catalog')); } catch { /* noop */ }
  },
  /** 효과음 하나를 카탈로그에서 뺀다 — 이후 그 id 를 부르면 소리 없이 넘어간다. */
  forgetSfx(id) {
    SFX_GROUPS.forEach((g) => {
      const at = g.items.findIndex((it) => it.id === id);
      if (at >= 0) g.items.splice(at, 1);
    });
    delete SOUND_CATALOG[id];
    sampleCache.delete(id);
    try { window.dispatchEvent(new Event('audio:catalog')); } catch { /* noop */ }
  },
  stopBgm() {
    bgmList = [];
    bgmSig = '';
    bgmPendingPlay = false;
    nowPlayingSrc = null;
    try { window.dispatchEvent(new CustomEvent('audio:bgm', { detail: { src: null } })); } catch (err) { /* noop */ }
    try { bgmEl?.pause(); } catch (err) { /* noop */ }
    syncMediaSession();
  },
  // 사운드 탭 "지금 재생 중" 표시용 — mp3 경로('/audio/bgm/xxx.mp3') 반환
  getNowPlaying() {
    return nowPlayingSrc;
  },
};
